import React, { useCallback, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DateTime } from 'luxon';
import { useMessages } from 'modules/intl';
import { useTimesheetFrame } from '../../embeddedTimesheet/useTimesheetFrame';
import { SmartDiaryEntry } from '../type';
import {
  useApplyToTimsheet,
  useSmartDiaryDirtyState,
  SyncResult,
  useSmartDiaryCount
} from './hooks';
import { LastSyncedTime } from './LastSyncedTime';
import { TimesheetSnackBar } from './TimesheetSnackBar';
import { ErrorDialog } from './ErrorDialog';
import { useSmartDiaryLastSyncErrors } from './hooks/useSmartDiaryLastSyncErrors';
import { ApplyProgress } from './ApplyProgress';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 2, 1.5)
  },
  button: {
    padding: theme.spacing(1, 4, 1, 4)
  }
}));

type ApplyToTimesheetProps = {
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>;
  filteredSmartEntries: SmartDiaryEntry[];
  smartDiarySyncCount: number;
  setSmartDiarySyncCount: (value: number) => void;
  lastSyncTime?: DateTime;
};

const messageIds = [
  'smartDiary.applyToTimesheet',
  'smartDiary.applyNToTimesheet'
];

export const ApplyToTimesheet: React.FC<ApplyToTimesheetProps> = ({
  smartEntriesByDay,
  filteredSmartEntries,
  lastSyncTime,
  smartDiarySyncCount,
  setSmartDiarySyncCount
}: ApplyToTimesheetProps) => {
  const classes = useStyles();

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [openErrorDialog, setOpenErrorDialog] = React.useState(false);
  const [successCount, setSuccessCount] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);

  const toSyncCount = useSmartDiaryCount(filteredSmartEntries);
  const { onApplyToTimesheet, syncDataKey } = useApplyToTimsheet(
    filteredSmartEntries
  );
  const { reloadTimesheet } = useTimesheetFrame();
  const { isDirty } = useSmartDiaryDirtyState({
    smartEntries: filteredSmartEntries
  });
  const {
    lastSyncErrors,
    updateLastSyncErrors
  } = useSmartDiaryLastSyncErrors();

  const onComplete = useCallback(
    (syncResult: SyncResult) => {
      setShowSnackbar(true);
      setSuccessCount(syncResult.successCount);
      setTotalCount(syncResult.totalCount);
      setSmartDiarySyncCount(smartDiarySyncCount + 1);
      updateLastSyncErrors(filteredSmartEntries, syncResult.errors);
    },
    [
      filteredSmartEntries,
      setSmartDiarySyncCount,
      smartDiarySyncCount,
      updateLastSyncErrors
    ]
  );

  useEffect(() => {
    const storedSyncData = sessionStorage.getItem(syncDataKey);
    if (storedSyncData) {
      const syncResult = JSON.parse(storedSyncData) as SyncResult;
      if (syncResult) {
        onComplete(syncResult);
      }
      sessionStorage.removeItem(syncDataKey);
    }
  }, [syncDataKey, onComplete]);

  const onClick = useCallback(async () => {
    setIsProcessing(true);
    setShowSnackbar(false);
    await onApplyToTimesheet(filteredSmartEntries, onComplete);
    reloadTimesheet();
    setIsProcessing(false);
  }, [onApplyToTimesheet, filteredSmartEntries, onComplete, reloadTimesheet]);

  const btnMessages = useMessages(messageIds, { count: toSyncCount });
  return (
    <>
      <Box className={classes.wrapper}>
        <LastSyncedTime
          lastSyncTime={lastSyncTime}
          lastSyncErrors={lastSyncErrors}
          setOpenErrorDialog={setOpenErrorDialog}
        />
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={onClick}
          disabled={!isDirty}
          data-testid={'smart-diary-apply-to-timesheet-button'}
        >
          {toSyncCount
            ? btnMessages['smartDiary.applyNToTimesheet']
            : btnMessages['smartDiary.applyToTimesheet']}
        </Button>
      </Box>
      <ApplyProgress isProcessing={isProcessing} />
      <ErrorDialog
        open={openErrorDialog}
        setOpen={setOpenErrorDialog}
        lastSyncErrors={lastSyncErrors}
        smartEntriesByDay={smartEntriesByDay}
      />
      <TimesheetSnackBar
        showSnackbar={showSnackbar}
        successCount={successCount}
        totalCount={totalCount}
        setOpenErrorDialog={setOpenErrorDialog}
      />
    </>
  );
};
